import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { onboardingApi } from '../lib/superAdminApi';
import mainApi from '../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Sparkles, ChevronRight, ChevronLeft, Check, Loader2, Building2, ShieldCheck, Bell, Package, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

const SECTORS = [
  { v: 'banking', l: 'Banca / Serviços financeiros' },
  { v: 'energy', l: 'Energia / Utilities' },
  { v: 'health', l: 'Saúde' },
  { v: 'telecoms', l: 'Telecomunicações' },
  { v: 'transport', l: 'Transportes' },
  { v: 'water', l: 'Águas' },
  { v: 'digital', l: 'Infraestrutura digital / SaaS' },
  { v: 'government', l: 'Administração pública' },
  { v: 'manufacturing', l: 'Indústria / Manufactura' },
  { v: 'other', l: 'Outro' },
];

const STEPS = [
  { key: 'company', title: 'A tua empresa', icon: Building2 },
  { key: 'scope', title: 'Âmbito de conformidade', icon: ShieldCheck },
  { key: 'notifications', title: 'Notificações', icon: Bell },
  { key: 'templates', title: 'Templates iniciais', icon: Package },
];

export default function OnboardingWizard() {
  const { user, isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(null);   // null = all selected by default
  const [data, setData] = useState({
    step1: { company_name: '', sector: '', company_size: '11-50', country: 'PT', ciso_email: '', ciso_name: '', dpo_email: '', dpo_name: '' },
    step2: { compliance_mode: 'both', nis2_entity_type: '', ai_uses_high_risk: false, industries: [] },
    step3: { email_notifications: true, slack_webhook_url: '', teams_webhook_url: '', notify_on_incidents: true, notify_on_task_deadlines: true, notify_on_compliance_deadlines: true },
  });

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (!['admin', 'ciso', 'dpo'].includes(user.role)) return;
    onboardingApi.status().then(({ data: s }) => {
      if (!s.completed) {
        setData((d) => ({
          ...d,
          step1: { ...d.step1, company_name: s.tenant_name || '' },
          step2: { ...d.step2, compliance_mode: s.tenant_compliance_mode || 'both' },
        }));
        setVisible(true);
      }
    }).catch(() => {});
  }, [isAuthenticated, user]);

  const patch = (stepKey, k, v) => setData((d) => ({ ...d, [stepKey]: { ...d[stepKey], [k]: v } }));

  const next = () => {
    if (step === 0) {
      if (!data.step1.company_name || !data.step1.sector) return toast.error('Preenche o nome da empresa e sector');
    }
    if (step === 1) {
      if (!data.step2.compliance_mode) return toast.error('Escolhe o âmbito');
    }
    if (step === 2) {
      // Load preview before showing step 4
      loadPreview();
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const loadPreview = async () => {
    try {
      const params = new URLSearchParams({
        sector: data.step1.sector, compliance_mode: data.step2.compliance_mode,
        ai_uses_high_risk: String(!!data.step2.ai_uses_high_risk),
      });
      const { data: prev } = await mainApi.get(`/onboarding/preview-templates?${params}`);
      setPreview(prev);
      // Default: all selected
      const all = [...prev.policies, ...prev.evidences, ...prev.tasks].map((i) => i.template_key);
      setSelectedKeys(new Set(all));
    } catch (err) { toast.error('Falha a carregar preview'); }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...data };
      if (selectedKeys) payload.selected_template_keys = Array.from(selectedKeys);
      await onboardingApi.complete(payload);
      toast.success('Setup completo! Bem-vindo ao NIS2AI.');
      setVisible(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao guardar');
    } finally { setLoading(false); }
  };

  const skip = async () => {
    if (!window.confirm('Tens a certeza que queres saltar? Podes configurar depois em Definições.')) return;
    setLoading(true);
    try { await onboardingApi.skip(); setVisible(false); toast.info('Saltaste o setup. Podes completar em Definições.'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Falha'); }
    finally { setLoading(false); }
  };

  if (!visible) return null;

  const CurrentIcon = STEPS[step].icon;

  return (
    <Dialog open={visible} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0" data-testid="onboarding-wizard">
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-white text-xl">Vamos configurar o teu NIS2AI</DialogTitle>
              <DialogDescription className="text-white/80">Alguns passos rápidos e estás pronto</DialogDescription>
            </div>
          </div>

          {/* Progress dots */}
          <div className="mt-6 flex items-center gap-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex items-center flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    i < step ? 'bg-emerald-400 text-white' : i === step ? 'bg-white text-purple-600' : 'bg-white/20 text-white/60'
                  }`}>
                    {i < step ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-1 mx-1 rounded ${i < step ? 'bg-emerald-400' : 'bg-white/20'}`} />}
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-sm font-medium flex items-center gap-2"><CurrentIcon className="w-4 h-4" /> Passo {step + 1} / {STEPS.length}: {STEPS[step].title}</div>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {step === 0 && <Step1 data={data.step1} patch={(k, v) => patch('step1', k, v)} />}
          {step === 1 && <Step2 data={data.step2} patch={(k, v) => patch('step2', k, v)} />}
          {step === 2 && <Step3 data={data.step3} patch={(k, v) => patch('step3', k, v)} />}
          {step === 3 && <Step4Templates preview={preview} selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys} />}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
          <Button variant="ghost" onClick={skip} disabled={loading} data-testid="onboarding-skip-btn">Saltar setup</Button>
          <div className="flex gap-2">
            {step > 0 && <Button variant="outline" onClick={prev} data-testid="onboarding-prev-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Voltar</Button>}
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white" data-testid="onboarding-next-btn">
                Continuar <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={loading} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white" data-testid="onboarding-finish-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Terminar setup</>}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function Step1({ data, patch }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Diz-nos algumas coisas sobre a tua organização para adaptarmos os templates.</p>
      <div>
        <Label>Nome da empresa *</Label>
        <Input value={data.company_name} onChange={(e) => patch('company_name', e.target.value)} placeholder="ACME Corporation" data-testid="onboarding-company-name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Sector *</Label>
          <Select value={data.sector} onValueChange={(v) => patch('sector', v)}>
            <SelectTrigger data-testid="onboarding-sector"><SelectValue placeholder="Escolher sector…" /></SelectTrigger>
            <SelectContent>{SECTORS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Dimensão</Label>
          <Select value={data.company_size} onValueChange={(v) => patch('company_size', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1-10">1–10</SelectItem>
              <SelectItem value="11-50">11–50</SelectItem>
              <SelectItem value="51-250">51–250</SelectItem>
              <SelectItem value="251-1000">251–1000</SelectItem>
              <SelectItem value="1000+">1000+</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="pt-2 border-t">
        <p className="text-xs font-medium text-slate-500 mb-2">Contactos-chave (opcional)</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Nome do CISO</Label><Input value={data.ciso_name} onChange={(e) => patch('ciso_name', e.target.value)} /></div>
          <div><Label className="text-xs">Email do CISO</Label><Input type="email" value={data.ciso_email} onChange={(e) => patch('ciso_email', e.target.value)} /></div>
          <div><Label className="text-xs">Nome do DPO</Label><Input value={data.dpo_name} onChange={(e) => patch('dpo_name', e.target.value)} /></div>
          <div><Label className="text-xs">Email do DPO</Label><Input type="email" value={data.dpo_email} onChange={(e) => patch('dpo_email', e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

function Step2({ data, patch }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Que legislação te aplica? Só vamos mostrar os módulos relevantes.</p>
      <div>
        <Label>Âmbito</Label>
        <Select value={data.compliance_mode} onValueChange={(v) => patch('compliance_mode', v)}>
          <SelectTrigger data-testid="onboarding-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="both">NIS2 + EU AI Act (recomendado)</SelectItem>
            <SelectItem value="nis2_only">Só NIS2</SelectItem>
            <SelectItem value="ai_act_only">Só EU AI Act</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {(data.compliance_mode === 'both' || data.compliance_mode === 'nis2_only') && (
        <div>
          <Label>Classificação NIS2</Label>
          <Select value={data.nis2_entity_type} onValueChange={(v) => patch('nis2_entity_type', v)}>
            <SelectTrigger><SelectValue placeholder="Escolher…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="essencial">Entidade essencial (Anexo I)</SelectItem>
              <SelectItem value="importante">Entidade importante (Anexo II)</SelectItem>
              <SelectItem value="exempt">Não abrangida / a confirmar</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">Se tiveres dúvidas, escolhe "a confirmar" — mostramos-te um wizard depois.</p>
        </div>
      )}
      {(data.compliance_mode === 'both' || data.compliance_mode === 'ai_act_only') && (
        <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
          <Checkbox
            id="ai-high-risk"
            checked={data.ai_uses_high_risk}
            onCheckedChange={(v) => patch('ai_uses_high_risk', !!v)}
            data-testid="onboarding-ai-high-risk"
          />
          <div className="flex-1">
            <Label htmlFor="ai-high-risk" className="cursor-pointer">Usamos sistemas de IA em áreas de alto risco</Label>
            <p className="text-xs text-slate-600 mt-1">Ex: recrutamento, crédito, biometria, saúde. Marca se aplicável — activamos os módulos Anexo IV, Transparência e Supervisão Humana.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Step3({ data, patch }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Como queres receber alertas de conformidade?</p>
      <div className="flex items-start gap-3 p-3 rounded-lg border">
        <Checkbox id="email-notif" checked={data.email_notifications} onCheckedChange={(v) => patch('email_notifications', !!v)} data-testid="onboarding-email-notif" />
        <div className="flex-1"><Label htmlFor="email-notif" className="cursor-pointer">Emails para prazos, incidentes e vulnerabilidades críticas</Label></div>
      </div>
      <div>
        <Label className="text-slate-700">Slack webhook (opcional)</Label>
        <Input value={data.slack_webhook_url} onChange={(e) => patch('slack_webhook_url', e.target.value)} placeholder="https://hooks.slack.com/services/…" data-testid="onboarding-slack" />
        <p className="text-xs text-slate-500 mt-1">Enviamos alertas de tarefas críticas para este canal.</p>
      </div>
      <div>
        <Label className="text-slate-700">Microsoft Teams webhook (opcional)</Label>
        <Input value={data.teams_webhook_url} onChange={(e) => patch('teams_webhook_url', e.target.value)} placeholder="https://outlook.office.com/webhook/…" data-testid="onboarding-teams" />
      </div>
      <div className="pt-2 border-t space-y-2">
        <p className="text-xs font-medium text-slate-500">Quero receber alertas de:</p>
        <div className="flex items-center gap-2"><Checkbox id="n1" checked={data.notify_on_incidents} onCheckedChange={(v) => patch('notify_on_incidents', !!v)} /><Label htmlFor="n1" className="text-sm cursor-pointer">Incidentes de segurança</Label></div>
        <div className="flex items-center gap-2"><Checkbox id="n2" checked={data.notify_on_task_deadlines} onCheckedChange={(v) => patch('notify_on_task_deadlines', !!v)} /><Label htmlFor="n2" className="text-sm cursor-pointer">Prazos de tarefas</Label></div>
        <div className="flex items-center gap-2"><Checkbox id="n3" checked={data.notify_on_compliance_deadlines} onCheckedChange={(v) => patch('notify_on_compliance_deadlines', !!v)} /><Label htmlFor="n3" className="text-sm cursor-pointer">Prazos regulatórios (revisões, auditorias)</Label></div>
      </div>
    </div>
  );
}


function Step4Templates({ preview, selectedKeys, setSelectedKeys }) {
  if (!preview) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  const toggle = (key) => {
    const next = new Set(selectedKeys || []);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelectedKeys(next);
  };
  const isSel = (key) => selectedKeys?.has(key);
  const groupSrc = (src) => src === 'ai_act' ? 'AI Act' : src === 'sector' ? 'Sector' : 'NIS2';

  const allKeys = [...preview.policies, ...preview.evidences, ...preview.tasks].map((i) => i.template_key);
  const selectAll = () => setSelectedKeys(new Set(allKeys));
  const selectNone = () => setSelectedKeys(new Set());

  const renderList = (items, colorClass) => (
    <div className="space-y-2">
      {items.map((it) => (
        <label key={it.template_key} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
          <Checkbox checked={isSel(it.template_key)} onCheckedChange={() => toggle(it.template_key)} className="mt-0.5" data-testid={`tpl-check-${it.template_key}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900">{it.title}</span>
              <Badge variant="outline" className={`text-xs ${colorClass}`}>{groupSrc(it.source)}</Badge>
            </div>
            {it.description && <p className="text-xs text-slate-600 mt-1">{it.description}</p>}
            {it.requirement && <p className="text-xs text-slate-500 mt-1">📋 {it.requirement}</p>}
            {it.priority && <p className="text-xs text-slate-500 mt-1">⚠️ Prioridade: {it.priority} · ⏰ {it.days_until_due}d</p>}
          </div>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="onboarding-step-templates">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-700 font-medium">Templates para <b>{preview.sector_label}</b></p>
          <p className="text-xs text-slate-500">Escolhe o que queres que criemos automaticamente. Podes sempre criar mais depois.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={selectAll} data-testid="tpl-select-all"><CheckCheck className="w-3 h-3 mr-1" /> Todos</Button>
          <Button size="sm" variant="outline" onClick={selectNone} data-testid="tpl-select-none">Nenhum</Button>
        </div>
      </div>

      <div className="text-xs text-indigo-600 font-medium">
        {(selectedKeys?.size ?? 0)} de {allKeys.length} seleccionados
      </div>

      <details open className="border rounded-lg p-3">
        <summary className="font-medium cursor-pointer text-slate-800">📄 Políticas ({preview.policies.length})</summary>
        <div className="mt-3">{renderList(preview.policies, 'text-blue-700 border-blue-300')}</div>
      </details>
      <details className="border rounded-lg p-3">
        <summary className="font-medium cursor-pointer text-slate-800">📁 Evidências ({preview.evidences.length})</summary>
        <div className="mt-3">{renderList(preview.evidences, 'text-emerald-700 border-emerald-300')}</div>
      </details>
      <details className="border rounded-lg p-3">
        <summary className="font-medium cursor-pointer text-slate-800">✅ Tarefas ({preview.tasks.length})</summary>
        <div className="mt-3">{renderList(preview.tasks, 'text-amber-700 border-amber-300')}</div>
      </details>
    </div>
  );
}

